import Icon from "@/components/ui/icon";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CreateStoriesFolderDialog } from "@/dialogs/create-stories-folder-dialog";
import { useAuthStoreShallow } from "@/features/authentication";
import { cn } from "@/lib/cn";
import { useGetCurrentUserQuery, useGetFoldersQuery } from "@/shared/services";
import type { IStoriesFolder } from "@/shared/types";
import { useLockedAction } from "@components/plan-subscription-lock";
import { useAppDispatch } from "@store";
import { closeDialog, openDialog } from "@store/dialogs";
import { useMemo } from "react";
import { toast } from "react-toastify";
import { useMoveFolderCtx } from "../ctx";

const FEATURE = "stories-folders-limit";

interface IProps {
  setIsOpen: (status: boolean) => void;
}

type TFolderWithChildren = IStoriesFolder & {
  children: TFolderWithChildren[];
};

export const MoveFolderFolders = ({ setIsOpen }: IProps) => {
  const dispatch = useAppDispatch();
  const { isAdmin, isUserOrCreatorLite } = useAuthStoreShallow((s) => ({
    isAdmin: s.isAdminOrAdministrator,
    isUserOrCreatorLite: s.isUserOrCreatorLite,
  }));
  const { data: folders = [], isLoading: foldersLoading } =
    useGetFoldersQuery();
  const { data: user, isLoading: userLoading } = useGetCurrentUserQuery();
  const { folderId } = useMoveFolderCtx();
  const { onLockedAction, value, plan } = useLockedAction({
    feature: FEATURE,
  });
  const hasLimitation = typeof value === "number";
  const isLocked =
    hasLimitation &&
    folders?.length &&
    folders?.length >= value &&
    value !== null;

  // Helper function to get all descendant folder IDs (to prevent circular moves)
  const getDescendantIds = (
    allFolders: IStoriesFolder[],
    parentId: string,
  ): Set<string> => {
    const descendants = new Set<string>();
    const findChildren = (id: string) => {
      allFolders.forEach((folder) => {
        if (folder.parentId === id) {
          descendants.add(folder._id);
          findChildren(folder._id);
        }
      });
    };
    findChildren(parentId);
    return descendants;
  };

  const nestedFolders = useMemo(() => {
    if (!folders.length) return [];

    // Get all descendant folder IDs to exclude them
    const descendantIds = getDescendantIds(folders, folderId);

    // Filter folders based on user permissions and exclude the selected folder and its descendants
    const userFolders = (
      isAdmin
        ? folders
        : folders.filter(
            (folder) =>
              folder.users.some(
                (u: { oktaId: string }) => u.oktaId === user?.oktaId,
              ) || folder.openAccess,
          )
    ).filter(
      (folder) => folder._id !== folderId && !descendantIds.has(folder._id),
    );

    // Build nested structure
    const buildNestedStructure = (
      allFolders: IStoriesFolder[],
      parentId: string | null | undefined,
    ): TFolderWithChildren[] => {
      return allFolders
        .filter((folder: IStoriesFolder) => {
          if (parentId === undefined) {
            return folder.parentId === null || folder.parentId === undefined;
          }
          return folder.parentId === parentId;
        })
        .map((folder: IStoriesFolder) => ({
          ...folder,
          children: buildNestedStructure(allFolders, folder._id),
        }));
    };

    return buildNestedStructure(userFolders, undefined);
  }, [folders, user, isAdmin, folderId]);

  if (foldersLoading || userLoading) return <Skeleton />;

  const handleCreateNewFolder = () => {
    if (isUserOrCreatorLite) {
      toast.info("Only administrators and creators can create a new folder.", {
        toastId: "create-folder-toast",
      });
      return;
    }
    if (isLocked) return onLockedAction();
    dispatch(closeDialog("move-folder"));
    dispatch(
      openDialog({
        id: CreateStoriesFolderDialog.dialogId,
        content: CreateStoriesFolderDialog,
        props: {},
      }),
    );
  };

  return (
    <ScrollArea maxHeight={248}>
      <div className="tw-p-2">
        <div className="tw-space-y-1">
          <div
            className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-rounded tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50"
            onClick={handleCreateNewFolder}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCreateNewFolder(); }}
            role="button"
            tabIndex={0}
          >
            <Icon icon="ri-add-line" className="tw-mr-2 tw-text-blue" />
            <span className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
              Add new folder
            </span>
            {!!isLocked && <PlanLockedLabel plan={plan} />}
          </div>

          <Folder id={""} name={"Stories"} setIsOpen={setIsOpen} />
          {nestedFolders?.map((folder) => (
            <RecursiveFolder
              key={folder._id}
              folder={folder}
              setIsOpen={setIsOpen}
            />
          ))}
        </div>
      </div>
    </ScrollArea>
  );
};

interface IFolderProps {
  id: string;
  name: string;
  setIsOpen: (status: boolean) => void;
}

const Folder = ({ setIsOpen, id, name }: IFolderProps) => {
  const { folder: selected, setFolder } = useMoveFolderCtx();
  const isActive = selected._id === id;
  return (
    <div
      onClick={() => {
        setFolder({ _id: id, name });
        setIsOpen(false);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setFolder({ _id: id, name }); setIsOpen(false); } }}
      role="button"
      tabIndex={0}
      className={cn(
        "tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-rounded tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
        isActive && "tw-bg-primary-50 tw-text-primary hover:tw-bg-primary-50",
      )}
    >
      <span className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
        {name}
      </span>
    </div>
  );
};

const RecursiveFolder = ({
  folder,
  level = 0,
  setIsOpen,
}: {
  folder: TFolderWithChildren;
  level?: number;
  setIsOpen: (status: boolean) => void;
}) => {
  const { folder: selected, setFolder } = useMoveFolderCtx();
  const isActive = selected._id === folder._id;
  const paddingLeft = 36 + level * 16;

  return (
    <>
      <div
        className={cn(
          "tw-mt-1 tw-flex tw-h-9 tw-max-w-[448px] tw-cursor-pointer tw-items-center tw-rounded tw-pr-2 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
          isActive && "tw-bg-primary-50 tw-text-primary",
        )}
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => {
          setFolder({ _id: folder._id, name: folder.name });
          setIsOpen(false);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setFolder({ _id: folder._id, name: folder.name }); setIsOpen(false); } }}
        role="button"
        tabIndex={0}
      >
        <span className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
          {folder.name}
        </span>
      </div>
      {folder.children?.map((childFolder) => (
        <RecursiveFolder
          key={childFolder._id}
          folder={childFolder}
          level={level + 1}
          setIsOpen={setIsOpen}
        />
      ))}
    </>
  );
};

const PlanLockedLabel = ({ plan }: { plan: string }) => {
  return (
    <span
      style={{ boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.10)" }}
      className={cn(
        "tw-ml-auto tw-flex tw-size-5 tw-items-center tw-justify-center tw-rounded-md",
        {
          "tw-bg-pro-base": plan === "pro",
          "tw-bg-team-base": plan === "team",
        },
      )}
    >
      <Icon
        icon="ri-vip-crown-fill"
        className="tw-size-3 tw-text-xs/none tw-text-white"
      />
    </span>
  );
};

const SKELETON_WIDTHS = [65, 72, 58, 78];

const Skeleton = () => {
  return (
    <div className="tw-w-[464px] tw-space-y-1 tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-p-2">
      {[1, 2, 3, 4].map((i) => (
        <div
          className="tw-flex tw-h-9 tw-animate-[pulse_.8s_linear_infinite] tw-items-center tw-px-3"
          key={i}
        >
          <span className="tw-mr-2 tw-block tw-size-4 tw-rounded-full tw-bg-skeleton-gradient"></span>
          <span
            className="tw-block tw-h-[6px] tw-rounded-xl tw-bg-skeleton-gradient"
            style={{ width: `${SKELETON_WIDTHS[i - 1]}%` }}
          ></span>
        </div>
      ))}
    </div>
  );
};
